<script setup lang="ts">
import {computed, ref} from "vue";
import Callout from "@/components/utils/Callout.vue";
import Tabs from "./utils/Tabs.vue";
import { useLocalStorage } from "@vueuse/core";
import calculateCompoundRoi from "@/lib/calculateCompoundRoi";
import { performanceHistory } from "@/lib/external-data/performanceHistory";
import formatRoi from "@/lib/formatRoi";
import {useI18n} from "vue-i18n";

const {t} = useI18n({
  useScope: 'global',
});

const selectedAudienceSize = ref<"top-10" | "all">("top-10");
const selectedPerformanceDisplay = useLocalStorage<"%" | "x">(
  "selected-performance-display",
  "x",
  { initOnMounted: true }
);
const compoundRoi = computed(() =>
  calculateCompoundRoi(performanceHistory[selectedAudienceSize.value], "auto")
);
</script>

<template>
  <section class="relative isolate pt-12 w-full flex flex-col items-center">
    <div class="absolute top-0 -z-5 w-max h-180 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0) delay-1000" data-aos>
      <slot name="background"></slot>
    </div>
    <div class="container flex flex-col items-center">
      <Callout
        data-aos
        class="duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-100"
        icon="i-mingcute:performance-line"
      >
        {{ t('proven_to_perform') }}
      </Callout>
      <h2
        data-aos
        :class="[
          'heading-2 mt-2.5 text-center',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200',
        ]"
      >
        <slot name="header" />
      </h2>
      <span
        data-aos
        :class="[
          'mt-4 leading-[150%] text-center text-brand-secondary',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-300',
        ]"
      >
        {{ t('see_how_dipsway_averages') }}
      </span>
      <Tabs
        data-aos
        class="duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400 relative z-1"
        :options="{'top-10': t('top_10_of_users'),'all': t('all_users')}"
        v-model="selectedAudienceSize"
      />
      <div
        data-aos
        :class="[
          'mt-5 font-bold text-12 relative -z-1',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-500',
        ]"
      >
        <!-- ligthing effect start -->
        <div
          :class="[
            'bg-effect-lighting bg-brand-gradient -z-5 opacity-70',
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'aspect-video w-240',
          ]"
        />
        <!-- ligthing effect end -->
        <Transition
          enter-active-class="duration-300 ease-in-out"
          leave-active-class="duration-300 ease-in-out"
          enter-from-class="translate-y-2 opacity-0"
          leave-to-class="-translate-y-2 opacity-0"
          mode="out-in"
        >
          <span
            :key="selectedPerformanceDisplay"
            class="text-brand-primary inline-block"
          >
            {{ formatRoi(compoundRoi.pnl, selectedPerformanceDisplay) }}
          </span>
        </Transition>
      </div>
      <span
        data-aos
        :class="[
          'text-lg font-semibold leading-[150%] text-center max-w-[35ch] h-14 lg:-mb-4 lt-lg:mb-4',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-600',
        ]"
      >
        <Transition
          enter-active-class="duration-400 delay-200 ease-in-out"
          leave-active-class="duration-100 delay-200 ease-in-out"
          enter-from-class="translate-y-2 opacity-0"
          leave-to-class="-translate-y-2 opacity-0"
          mode="out-in"
        >
          <span class="inline-block" v-if="selectedPerformanceDisplay === '%'">
            {{ t('last') }} {{ compoundRoi.period.lengthInMonths }} {{ t('months_compound_roi') }}
          </span>
          <span
            class="inline-block"
            v-else-if="selectedPerformanceDisplay === 'x'"
          >
            {{ t('total_return_based_on_last') }}
            {{ compoundRoi.period.lengthInMonths }} {{ t('months_compound_roi') }}
          </span>
        </Transition>
      </span>
      <!-- aria-friendly selectors -->
      <div
        data-aos
        class="duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-10) delay-700 inline-block lt-lg:mx-auto lg:ml-auto"
      >
        <!-- switch to multiplier -->
        <button
          :disabled="selectedPerformanceDisplay === 'x'"
          :aria-pressed="selectedPerformanceDisplay === 'x'"
          @click="selectedPerformanceDisplay = 'x'"
          :class="[
            'text-brand-gradient text-sm',
            selectedPerformanceDisplay === 'x' && 'sr-only focus:not-sr-only',
          ]"
        >
          {{ t('show_as_multiplier_values') }}
        </button>
        <!-- switch to percentage -->
        <button
          :disabled="selectedPerformanceDisplay === '%'"
          :aria-pressed="selectedPerformanceDisplay === '%'"
          @click="selectedPerformanceDisplay = '%'"
          :class="[
            'text-brand-gradient text-sm',
            selectedPerformanceDisplay === '%' && 'sr-only focus:not-sr-only',
          ]"
        >
          {{ t('show_as_percentage_values') }}
        </button>
      </div>
    </div>
    <div
      data-aos
      class="duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-10) delay-700"
    >
      <slot :selectedAudienceSize />
    </div>
  </section>
</template>
